import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  CreditCard, AlertTriangle, ChevronRight, Settings,
} from "lucide-react";

import { Card } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// FIN-1 — FaturasCard
// Lista cartões de crédito (accounts type=credit_card) com utilização +
// alert pra cartões sem closing_day/due_day. Footer Ver todas → Contas.

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

export default function FaturasCard({ onSeeAll, onConfigureCards }) {
  const { user, token } = useAuth();
  const userId = user?.id;
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId || !token) return;
    try {
      // FIN-1.1: JWT via Bearer (backend exige).
      const r = await fetch(`${API_URL}/api/accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        setAccounts(Array.isArray(data?.accounts) ? data.accounts : []);
      }
    } catch (_) { /* ignore */ }
    finally { setLoading(false); }
  }, [userId, token]);

  useEffect(() => { load(); }, [load]);

  const cards = accounts.filter(a => a.type === "credit_card");
  const unconfigured = cards.filter(a => !a.closing_day || !a.due_day);
  const totalOpen = cards.reduce((s, c) => {
    const used = Math.abs(Number(c.current_balance) || 0);
    return s + used;
  }, 0);

  return (
    <Card variant="elevated" className="glass-card glow-hover p-5 md:p-6 flex flex-col" data-testid="faturas-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <span className="text-caption font-semibold uppercase text-brand-cyan/80 inline-flex items-center gap-2" style={{ letterSpacing: "0.12em" }}>
            <span className="w-1.5 h-1.5 rounded-full bg-brand-cyan" aria-hidden="true" />
            Faturas
          </span>
          <p className="text-micro text-foreground-subtle mt-0.5 uppercase" style={{ letterSpacing: "0.08em" }}>
            A pagar
          </p>
        </div>
        <p className="text-h3 font-bold tabular-nums text-foreground">
          {fmtBRL(totalOpen)}
        </p>
      </div>

      {unconfigured.length > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-status-warning/10 border border-status-warning/20 mb-3">
          <AlertTriangle className="w-4 h-4 text-status-warning shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-caption text-status-warning">
              Configure as datas dos cartões — {unconfigured.length} cartão{unconfigured.length === 1 ? "" : "es"} sem data
            </p>
            <button
              type="button"
              onClick={onConfigureCards}
              className="text-micro text-brand-cyan hover:text-brand-cyan/80 transition-colors inline-flex items-center gap-1 mt-0.5"
            >
              <Settings className="w-3 h-3" />
              Configurar
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <ul className="space-y-2 flex-1">
          {[0, 1].map(i => (
            <li key={i} className="h-16 rounded-lg bg-surface-card-hover/40 animate-pulse" />
          ))}
        </ul>
      ) : cards.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center py-6 text-center">
          <div className="w-12 h-12 rounded-full bg-brand-cyan/10 flex items-center justify-center mb-3">
            <CreditCard className="w-5 h-5 text-brand-cyan" aria-hidden="true" />
          </div>
          <p className="text-sm font-medium text-foreground">Sem cartões cadastrados</p>
          <p className="text-caption text-foreground-muted mt-1">
            Adicione um cartão pra acompanhar faturas.
          </p>
        </div>
      ) : (
        <ul className="space-y-2 flex-1" data-testid="faturas-list">
          {cards.slice(0, 3).map((c, i) => {
            const limit = Number(c.credit_limit) || 0;
            const used = Math.abs(Number(c.current_balance) || 0);
            const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
            return (
              <motion.li
                key={c.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, duration: 0.2 }}
                className="px-3 py-2.5 rounded-lg border border-subtle bg-surface-card hover:border-strong transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-md bg-brand-cyan/10 flex items-center justify-center shrink-0">
                    <CreditCard className="w-4 h-4 text-brand-cyan" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {c.institution || c.name || "Cartão"}
                    </p>
                    <p className="text-micro text-foreground-subtle tabular-nums">
                      {fmtBRL(used)} de {fmtBRL(limit)}
                    </p>
                  </div>
                  <span className={cn(
                    "text-caption font-semibold tabular-nums shrink-0",
                    pct >= 80 ? "text-status-error" : pct >= 50 ? "text-status-warning" : "text-brand-green",
                  )}>
                    {pct}%
                  </span>
                </div>
                <div className="mt-2 h-1 bg-white/[0.04] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
                    className={cn(
                      "h-full rounded-full",
                      pct >= 80 ? "bg-status-error" : pct >= 50 ? "bg-status-warning" : "bg-brand-green",
                    )}
                  />
                </div>
              </motion.li>
            );
          })}
        </ul>
      )}

      <div className="mt-4 pt-3 border-t border-subtle">
        <button
          type="button"
          onClick={onSeeAll}
          className="inline-flex items-center gap-1 text-caption text-brand-cyan hover:text-brand-cyan/80 transition-colors"
          data-testid="faturas-see-all"
        >
          Ver todas
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </Card>
  );
}
